import { Badge } from "@designs-by-maryam/ui/components/badge";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@designs-by-maryam/ui/components/table";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Palette, Shield, Users } from "lucide-react";
import { requireAdmin } from "@/functions/require-role";
import { orpc } from "@/utils/orpc";

export const Route = createFileRoute("/_dashboard/admin/")({
	component: AdminDashboard,
	beforeLoad: async () => {
		// Server-side role check - no client-side flash
		await requireAdmin();
	},
});

function AdminDashboard() {
	const listUsers = useQuery(orpc.listAllUsers.queryOptions());

	// Calculate role counts from users list
	const designerCount =
		listUsers.data?.users?.filter((user) => user.role === "designer").length ??
		0;

	return (
		<div className="container mx-auto p-6">
			<div className="mb-8">
				<h1 className="flex items-center gap-3 font-bold text-3xl">
					<Shield className="h-8 w-8 text-primary" />
					Admin Dashboard
				</h1>
				<p className="mt-2 text-muted-foreground">
					Manage users, designers, and system settings
				</p>
			</div>

			<div className="grid gap-6 md:grid-cols-3">
				{/* Users Card */}
				<div className="rounded-lg border bg-card p-6">
					<div className="mb-4 flex items-center gap-3">
						<div className="rounded-full bg-primary/10 p-3">
							<Users className="h-6 w-6 text-primary" />
						</div>
						<h2 className="font-semibold text-xl">Users</h2>
					</div>
					<p className="font-bold text-3xl">
						{listUsers.data?.users?.length ?? 0}
					</p>
					<p className="mt-2 text-muted-foreground text-sm">Total users</p>
				</div>

				{/* Designers Card */}
				<div className="rounded-lg border bg-card p-6">
					<div className="mb-4 flex items-center gap-3">
						<div className="rounded-full bg-primary/10 p-3">
							<Palette className="h-6 w-6 text-primary" />
						</div>
						<h2 className="font-semibold text-xl">Designers</h2>
					</div>
					<p className="font-bold text-3xl">{designerCount}</p>
					<p className="mt-2 text-muted-foreground text-sm">Active designers</p>
				</div>

				{/* Admin Info Card */}
				<div className="rounded-lg border bg-card p-6">
					<div className="mb-4 flex items-center gap-3">
						<div className="rounded-full bg-primary/10 p-3">
							<Shield className="h-6 w-6 text-primary" />
						</div>
						<h2 className="font-semibold text-xl">Admin Access</h2>
					</div>
					{listUsers.data?.adminUser && (
						<div className="space-y-1">
							<p className="font-medium text-sm">
								{listUsers.data.adminUser.email}
							</p>
							<p className="text-muted-foreground text-xs">
								Role: {listUsers.data.adminUser.role}
							</p>
						</div>
					)}
				</div>
			</div>

			{/* Users Table Section */}
			<div className="mt-6 rounded-lg border bg-card p-6">
				<h3 className="mb-4 font-semibold text-lg">System Users</h3>

				{listUsers.isLoading && (
					<p className="text-muted-foreground">Loading users...</p>
				)}

				{listUsers.error && (
					<div className="rounded-lg border border-destructive bg-destructive/10 p-4">
						<p className="font-medium text-destructive">Error loading users</p>
						<p className="mt-1 text-muted-foreground text-sm">
							{listUsers.error.message}
						</p>
					</div>
				)}

				{listUsers.data &&
					(listUsers.data.users && listUsers.data.users.length > 0 ? (
						<Table>
							<TableHeader>
								<TableRow>
									<TableHead>Name</TableHead>
									<TableHead>Email</TableHead>
									<TableHead>Role</TableHead>
									<TableHead>Status</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{listUsers.data.users.map(
									(user: {
										id: string;
										name?: string;
										email: string;
										role?: string;
										emailVerified?: boolean;
									}) => (
										<TableRow key={user.id}>
											<TableCell className="font-medium">
												{user.name || "—"}
											</TableCell>
											<TableCell>{user.email}</TableCell>
											<TableCell>
												<Badge
													variant={
														user.role === "admin" ? "default" : "secondary"
													}
												>
													{user.role || "user"}
												</Badge>
											</TableCell>
											<TableCell>
												<Badge
													variant={user.emailVerified ? "default" : "outline"}
												>
													{user.emailVerified ? "Verified" : "Pending"}
												</Badge>
											</TableCell>
										</TableRow>
									),
								)}
							</TableBody>
						</Table>
					) : (
						<p className="py-8 text-center text-muted-foreground">
							No users found. Users will appear here once they register.
						</p>
					))}
			</div>
		</div>
	);
}
